import React from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { formatDistanceToNow, parseJSON } from 'date-fns';
import { useParams } from 'react-router-dom';
import { useSupportCase } from '../../data/support/useSupportCustomer';
import CaseStatusChip from '../internal/support/CaseStatusChip';
import SupportCaseChat from './SupportCaseChat';

export default function SupportCaseDetail() {
  const { caseId } = useParams<{ caseId: string }>();
  const caseQuery = useSupportCase(caseId!);

  if (caseQuery.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (caseQuery.isError || !caseQuery.data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load case details</Alert>
      </Box>
    );
  }

  const supportCase = caseQuery.data;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="h5"
            component="span"
            sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
          >
            #{supportCase.case_number}
          </Typography>
          <Typography variant="h5">{supportCase.subject}</Typography>
          <CaseStatusChip value={supportCase.status} type="status" />
          <CaseStatusChip value={supportCase.priority} type="priority" />
        </Box>
        {supportCase.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
          >
            {supportCase.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Created{' '}
            {formatDistanceToNow(parseJSON(supportCase.created_at), {
              addSuffix: true,
            })}
          </Typography>
          {supportCase.assignee_user_id && (
            <Typography variant="body2" color="text.secondary">
              Assigned
            </Typography>
          )}
        </Box>
      </Box>

      {/* Chat */}
      <SupportCaseChat caseId={caseId!} />
    </Box>
  );
}
